import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, Observable, tap } from 'rxjs';
import { ApiResponse } from '../../../core/models/api-response.models';
import {
  AdminStrategyFormValue,
  AdminStrategyItem,
  AdminStrategyLookups,
  AdminStrategyPayload,
  AdminStrategyQueryParams,
  AdminStrategyResponse,
  FilterState,
} from './admin-strategy';
import { environment } from '../../../../environments/environment.development';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ErrorHandlingService } from '../../../core/services/error-handling.service';
import { BehaviorStrategyExcelService } from './behavior-strategy-excel.service';

@Injectable({
  providedIn: 'root',
})
export class AdminStrategyService {
  private readonly ADMIN_API_URL = `${environment.apiUrl}/AdminStrategy`;

  adminStrategiesSubject =
    new BehaviorSubject<ApiResponse<AdminStrategyResponse> | null>(null);
  adminStrategies$ = this.adminStrategiesSubject.asObservable();

  private isExporting = new BehaviorSubject<boolean>(false);
  isExporting$ = this.isExporting.asObservable();

  private currentQueryParams: AdminStrategyQueryParams | null = null;

  private totalRows = 0;

  private filterStateSubject = new BehaviorSubject<FilterState>({
    environments: [],
    ageLevels: [],
    functions: [],
    settingEvents: [],
    antecedents: [],
    languageLevel: [],
    behaviourConsequence: [],
    behaviourResuorceType: [],
    behaviourReplacementType: [],
    behaviourSupervisionType: [],
  });
  filterState$ = this.filterStateSubject.asObservable();

  constructor(
    private http: HttpClient,
    private errorHandler: ErrorHandlingService,
    private behaviorStrategyExcelService: BehaviorStrategyExcelService
  ) {}

  getAdminStrategyList(
    queryParams: AdminStrategyQueryParams
  ): Observable<ApiResponse<AdminStrategyResponse>> {
    this.currentQueryParams = queryParams;

    const paramConfig = {
      PageSize: queryParams.pageSize,
      Page: queryParams.pageIndex,
      Sort: queryParams.sortField,
      SortOrder: queryParams.sortOrder,
      AgeLevels: queryParams.ageLevels ?? [],
      Environments: queryParams.environments ?? [],
      Antecedents: queryParams.antecedents ?? [],
      Functions: queryParams.functions ?? [],
      Consequences: queryParams.consequences ?? [],
      StrategySettings: queryParams.strategySettings ?? [],
      LanguageLevelId: queryParams.languageLevel ?? [],
      Title: queryParams.searchTerm ?? '',
    };

    let params = new HttpParams();

    Object.entries(paramConfig).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        if (value.length > 0) {
          value.forEach(item => {
            params = params.append(key, item.toString());
          });
        }
      } else if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, value.toString());
      }
    });

    return this.http
      .get<
        ApiResponse<AdminStrategyResponse>
      >(`${this.ADMIN_API_URL}/list`, { params })
      .pipe(
        tap(response => {
          if (response?.result) {
            this.totalRows = response.result.totalRows;
            this.adminStrategiesSubject.next(response);
          }
        }),
        catchError(this.errorHandler.handleError)
      );
  }

  refreshTable() {
    if (this.currentQueryParams) {
      this.getAdminStrategyList(this.currentQueryParams).subscribe();
    }
  }

  exportStrategyData(): void {
    if (this.totalRows > 0 && this.currentQueryParams) {
      this.isExporting.next(true);

      const exportParams: AdminStrategyQueryParams = {
        ...this.currentQueryParams,
        pageSize: this.totalRows,
        pageIndex: 1,
      };

      const paramConfig = {
        PageSize: exportParams.pageSize,
        Page: exportParams.pageIndex,
        Sort: exportParams.sortField,
        SortOrder: exportParams.sortOrder,
        AgeLevels: exportParams.ageLevels ?? [],
        Environments: exportParams.environments ?? [],
        Antecedents: exportParams.antecedents ?? [],
        Functions: exportParams.functions ?? [],
        Consequences: exportParams.consequences ?? [],
        StrategySettings: exportParams.strategySettings ?? [],
        LanguageLevelId: exportParams.languageLevel ?? [],
        Title: exportParams.searchTerm ?? '',
      };

      let params = new HttpParams();

      Object.entries(paramConfig).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            value.forEach(item => {
              params = params.append(key, item.toString());
            });
          }
        } else if (value !== undefined && value !== null && value !== '') {
          params = params.set(key, value.toString());
        }
      });

      this.http
        .get<ApiResponse<AdminStrategyResponse>>(`${this.ADMIN_API_URL}/list`, {
          params,
        })
        .pipe(catchError(this.errorHandler.handleError))
        .subscribe({
          next: response => {
            if (response?.result?.items) {
              this.behaviorStrategyExcelService.exportStrategiesToExcel(
                response.result.items,
                'Behavior_Strategies_Export'
              );
            }
          },
          complete: () => {
            this.isExporting.next(false);
          },
        });
    }
  }

  getLookups(): Observable<ApiResponse<AdminStrategyLookups>> {
    return this.http
      .get<ApiResponse<AdminStrategyLookups>>(`${this.ADMIN_API_URL}/lookups`)
      .pipe(
        tap(response => {
          if (response?.result) {
            this.updateFilterState(response.result);
          }
        }),
        catchError(this.errorHandler.handleError)
      );
  }

  private updateFilterState(lookups: AdminStrategyLookups): void {
    const newState = {
      environments: lookups.behaviourEnvironment,
      ageLevels: lookups.behaviourAgeLevel,
      functions: lookups.behaviourFunctions,
      settingEvents: lookups.behaviourSettingEvents,
      antecedents: lookups.behaviourAntecedents,
      languageLevel: lookups.behaviourLanguageLevel,
      behaviourConsequence: lookups.behaviourConsequence,
      behaviourResuorceType: lookups.behaviourResuorceType,
      behaviourReplacementType: lookups.behaviourReplacementType,
      behaviourSupervisionType: lookups.behaviourSupervisionType,
    };

    this.filterStateSubject.next(newState);
  }

  updateSequenceNumbers(payload: {
    strategySequenceNumbers: { id: number; sequenceNumber: number }[];
  }): Observable<ApiResponse<AdminStrategyResponse>> {
    return this.http
      .put<
        ApiResponse<AdminStrategyResponse>
      >(`${this.ADMIN_API_URL}/update-sequence-number`, payload)
      .pipe(catchError(this.errorHandler.handleError));
  }

  createAdminStrategy(
    payload: AdminStrategyPayload
  ): Observable<ApiResponse<AdminStrategyResponse>> {
    return this.http.post<ApiResponse<AdminStrategyResponse>>(
      `${this.ADMIN_API_URL}/create`,
      payload
    );
  }

  updateAdminStrategy(
    payload: AdminStrategyPayload
  ): Observable<ApiResponse<AdminStrategyResponse>> {
    return this.http.post<ApiResponse<AdminStrategyResponse>>(
      `${this.ADMIN_API_URL}/update`,
      payload
    );
  }

  deleteAdminStrategy(
    id: number
  ): Observable<ApiResponse<AdminStrategyResponse>> {
    return this.http
      .delete<ApiResponse<AdminStrategyResponse>>(`${this.ADMIN_API_URL}/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  mapFormToPayload(
    formValue: AdminStrategyFormValue,
    id = 0
  ): AdminStrategyPayload {
    return {
      id,
      title: formValue.details.title,
      description: formValue.details.description,
      isSettingEvent: formValue.details.isSettingEvent,
      suitableForSchool: formValue.details.suitableForSchool,
      supervisionTypeId: Number(formValue.details.supervisionType),
      languageLevelId: Number(formValue.details.languageLevel),
      behaviourStrategyTypeId: Number(formValue.details.resourceType),
      strategyReplacementBehaviorTypes: formValue.details.replacementType
        ? formValue.details.replacementType
        : [],
      behaviorStrategySettings: formValue.predicates.settingEvents
        ? formValue.predicates.settingEvents
        : [],
      behaviorStrategyAntecedents: formValue.predicates.antecedents
        ? formValue.predicates.antecedents
        : [],
      behaviorStrategyEnvironments: formValue.predicates.environment
        ? formValue.predicates.environment
        : [],
      behaviorStrategyConsequences: formValue.predicates.consequences
        ? formValue.predicates.consequences
        : [],
      behaviourStrategyFunctions: formValue.predicates.functions
        ? formValue.predicates.functions
        : [],
      behaviourStrategyAgeLevels: formValue.predicates.ageLevel
        ? formValue.predicates.ageLevel
        : [],
      guidelines: formValue.guidelines.items.map(item => ({
        guideline: item.guideline,
        sequenceNumber: Number(item.sequence),
      })),
      detailPrompts: formValue.prompts.items.map(item => ({
        detailPrompt: item.detailPrompt,
        sequenceNumber: Number(item.sequence),
      })),
    };
  }

  mapResponseToFormValue(response: AdminStrategyItem): AdminStrategyFormValue {
    return {
      details: {
        resourceType: response.behaviourStrategyTypeId,
        replacementType:
          response.behaviourStrategyReplacementBehaviourTypes?.map(
            level => level.id
          ) ||
          null ||
          null,
        title: response.title,
        description: response.description,
        languageLevel: response.languageLevelId,
        supervisionType: response.supervisionTypeId,
        isSettingEvent: response.isSettingEvent,
        suitableForSchool: response.suitableForSchool,
      },
      predicates: {
        ageLevel:
          response.behaviourStrategyAgeLevels?.map(level => level.id) || null,
        environment:
          response.behaviourStrategyEnvironments?.map(item => item.id) || null,
        functions:
          response.behaviourStrategyFunctions?.map(item => item.id) || null,
        settingEvents:
          response.behaviourStrategySettings?.map(item => item.id) || null,
        antecedents:
          response.behaviourStrategyAntecedents.map(item => item.id) || null,
        consequences:
          response.behaviourStrategyConsequences.map(item => item.id) || null,
      },
      guidelines: {
        items: response.guidelines
          .sort((a, b) => a.sequenceNumber - b.sequenceNumber)
          .map(g => ({
            sequence: g.sequenceNumber,
            guideline: g.guideline,
          })),
      },
      prompts: {
        items: response.behaviorStrategyDetailPrompts
          .sort((a, b) => a.sequenceNumber - b.sequenceNumber)
          .map(p => ({
            key: p.id.toString(),
            sequence: p.sequenceNumber.toString(),
            detailPrompt: p.detailPrompt,
            action: '',
          })),
      },
    };
  }
}
